/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from 'react';
import { StyledPanel, PanelContent, ActionButtons, PanelOverlay, FavoriteToggleButton } from './styled';
import { ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { ApolloProvider } from '@apollo/client';
import { getDeployedEnvType, MiApolloClient } from '@marriott/mi-apollo-client-utils';
import { CloseButton } from './styled';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { HQV_ACTIONS, useHQVContext } from '@marriott/mi-shop-components';
import clsx from 'clsx';
import hqvOperationSignatures from './hqvOperationSignatures.json';
import { useSearchResultsRenderedDataStore } from '../../modules/store/searchResultsRenderedDataStore';
import { useSavedProperties } from '../../hooks/useSavedProperties';
import { useSessionUpdateZustandStore } from '../../modules/store/sessionUpdateZustandStore';
import { AI_SEARCH_TRACK_LABELS } from '../../constants/aiSearchTrackLabels';
import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';

/** Align with mi-shop-components PageonModal / Modal HQV labelling */
const HQV_MODAL_ID = 'hqv-modal';
const HQV_MODAL_HEADING_ID = 'hqv-modal-heading';

interface PropertyData {
  property?: {
    basicInformation?: {
      name?: string;
      description?: string;
    };
  };
  basicInformation?: {
    name?: string;
    description?: string;
  };
  node?: {
    property?: {
      basicInformation?: {
        name?: string;
        description?: string;
      };
    };
  };
  [key: string]: any;
}

interface HotelDetailsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  propertyData: PropertyData | null;
  propertyId: string;
  responsiveGridModelProps?: any;
  isMobile?: boolean;
  /** True when response contains a single property (e.g., availability_calendar_search) - hides rates */
  isSinglePropertyResponse?: boolean;
}

export const HotelDetailsPanel: React.FC<HotelDetailsPanelProps> = React.memo(
  ({ isMobile, isOpen, onClose, propertyData, propertyId, responsiveGridModelProps, isSinglePropertyResponse }) => {
    const apolloClient = useMemo(() => MiApolloClient(hqvOperationSignatures, getDeployedEnvType()), []);
    const { hqvDispatch } = useHQVContext();

    // Get session data from store
    const sessionObject = useSessionUpdateZustandStore(state => state.sessionObject);
    const jsonContent = useSearchResultsRenderedDataStore(state => state.jsonContent);
    const requestId = (jsonContent as any)?.envVarsObject?.['REQUEST_ID'] || '';

    // Debug logging
    console.log('[HotelDetailsPanel] sessionObject from store:', sessionObject);
    console.log('[HotelDetailsPanel] jsonContent.sessionData:', (jsonContent as any)?.sessionData);

    // Use the useSavedProperties hook
    const { isPropertySaved: checkIsPropertySaved, toggleSaveProperty: toggleSave } = useSavedProperties({
      pageVal: 'chat',
      requestId,
    });

    // Check if property is saved
    const isPropertySaved = useMemo(() => {
      return checkIsPropertySaved(propertyId);
    }, [checkIsPropertySaved, propertyId]);

    const hotelDetailsTitle = useMemo(() => {
      return (
        propertyData?.property?.basicInformation?.name ||
        propertyData?.basicInformation?.name ||
        propertyData?.node?.property?.basicInformation?.name ||
        'Hotel details'
      );
    }, [propertyData]);

    // Toggle save property
    const toggleSaveProperty = () => {
      if (!propertyId) {
        return;
      }
      toggleSave(propertyId);
    };

    useEffect(() => {
      hqvDispatch({
        type: HQV_ACTIONS.PROPERTY_IDENTITY,
        payload: propertyId || propertyData?.['propertyId'],
      });
    }, [hqvDispatch, propertyId, propertyData?.['propertyId']]);

    const panelRef = useRef<HTMLDivElement>(null);
    const dialogPanelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const isDialogActive = isOpen && Boolean(propertyData);

    useDialogFocusTrap(isDialogActive, dialogPanelRef, {
      initialFocusRef: closeButtonRef,
    });

    // Handle escape key
    useEffect(() => {
      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape' && isOpen) {
          onClose();
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscapeKey);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
        hqvDispatch({
          type: HQV_ACTIONS.PROPERTY_IDENTITY,
          payload: propertyId || propertyData?.['propertyId'],
        });
      } else {
        document.body.style.overflow = 'unset';
      }

      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = 'unset';
      };
    }, [isOpen, onClose, hqvDispatch, propertyId, propertyData?.['propertyId']]);

    // Click outside to close
    // useEffect(() => {
    //   const handleClickOutside = (event: MouseEvent) => {
    //     if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
    //       onClose();
    //     }
    //   };

    //   if (isOpen) {
    //     document.addEventListener('mousedown', handleClickOutside);
    //   }

    //   return () => {
    //     document.removeEventListener('mousedown', handleClickOutside);
    //   };
    // }, [isOpen, onClose]);

    // Don't render anything if there's no property data
    if (!propertyData) {
      return null;
    }

    return (
      <>
        <PanelOverlay isOpen={isOpen} onClick={onClose} aria-hidden="true" />
        <StyledPanel
          ref={dialogPanelRef}
          id={HQV_MODAL_ID}
          isOpen={isOpen}
          className={clsx('px-0', isSinglePropertyResponse && 'hide-rates')}
          role="dialog"
          aria-labelledby={HQV_MODAL_HEADING_ID}
        >
          <h2 id={HQV_MODAL_HEADING_ID} className="sr-only">
            {hotelDetailsTitle}
          </h2>
          <div className={clsx('d-flex flex-column px-4')}>
            <ActionButtons className="d-flex justify-content-between align-items-center py-3">
              <CloseButton ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close hotel details">
                <span aria-hidden="true" className="icon-clear icon-l"></span>
              </CloseButton>
              <div className="d-flex gap-2">
                <FavoriteToggleButton
                  type="button"
                  aria-label="Favorites"
                  aria-pressed={isPropertySaved}
                  data-track-label={AI_SEARCH_TRACK_LABELS.CHAT_COMPONENT_FAVORITES}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (sessionObject?.accessToken) {
                      toggleSaveProperty();
                    }
                  }}
                >
                  <span
                    aria-hidden="true"
                    className={clsx(isPropertySaved ? 'icon-heart-fill' : 'icon-heart-outline', 'icon-l')}
                  ></span>
                </FavoriteToggleButton>
              </div>
            </ActionButtons>
          </div>
          <PanelContent ref={panelRef} className={clsx(isMobile ? 'px-0' : 'px-4')}>
            <div>
              <ApolloProvider client={apolloClient}>
                <div
                  className="d-flex flex-column hotel-details-content"
                  style={{ height: '100vh', width: '100% !important', overflowX: 'hidden' }}
                >
                  <ResponsiveGrid
                    {...responsiveGridModelProps}
                    model={responsiveGridModelProps}
                    pagePath="/content/mi-aem-chat-spa"
                    itemPath="root/responsivegrid"
                    removeDefaultStyles={true}
                    customClassName="d-flex flex-column flex-grow-1"
                  />
                </div>
              </ApolloProvider>
            </div>
          </PanelContent>
        </StyledPanel>
      </>
    );
  }
);
